import moment from 'moment';
import React, { FormEvent, RefObject } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Segment } from '~src/interfaces/segment.interface';

import { FormHocProps, formHoc } from '~src/hocs/selectFirstInput';
import { AttrSlugType } from '~src/interfaces/attributes.types';
import { ScheduleI, SchedulePeriods, getScheduleStartDate } from '~src/interfaces/schedule.interface';
import { SurveySubjectTypes, SurveyUseCase } from '~src/interfaces/surveys.interfaces';
import { apiCallPost, apiCallPut } from '~src/modules/surveys/components/schedules/actions/backendActions';
import { Survey, SurveyScopes } from '~src/modules/surveys/interfaces';
import { StoreI } from '~src/rootReducer';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import { showNotification } from '~src/store/app/actions';
import { NotificationI, ShowNotification } from '~src/store/app/interfaces';
import { getCompanyAttrValueBySlug } from '~src/store/session/session.reducer';

import {
  CustomCycle,
  CycleI,
  CycleStatus,
  isCustomCycle,
  NewCustomCycle
} from '~src/interfaces/cycle.interface';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { fetchCycles } from '~src/shared/sidebar/actions/sidebarActions';
import { PeopleSectionContainer, SchedulesItemContent } from './scheduleTemplate.sc';
import AboutSection from './sections/AboutSection';
import AskSection from './sections/AskSection';
import ButtonSection from './sections/ButtonSection';
import CycleContainer from './sections/CycleContainer';
import ShareSection from './sections/ShareSection';
import { SurveyInvite } from './sections/SurveyInvite';
import {TFunction} from "i18next";
import {isAxiosError} from "~src/utils/isAxiosError";
import {differenceBy, eq} from "lodash";

interface StateProps {
  anonymityMinAnswers: number;
  customCycles: Record<string, CycleI>;
}

interface DispatchProps {
  showNotification: ShowNotification;
  fetchCycles: () => ApiReduxResp<CycleI[]>;
}

interface OwnProps {
  t: TFunction;
  schedule: ScheduleI;
  survey: Survey;
  fixedAddForm?: boolean;
  isAddSchedule: boolean;
}

type Props = OwnProps & StateProps & DispatchProps & FormHocProps & RouteComponentProps<object>;

export interface ScheduleErrors {
  ask: boolean;
  about: boolean;
  repetition: boolean;
  reminder: boolean;
  share: boolean;
  openEnded: boolean;
  deadline: boolean;
  unknownError?: boolean;
}

interface State {
  saveLoading: boolean;
  schedule: ScheduleI;
  selectedPeriodOption: SchedulePeriods;
  scheduleId: string | null;
  errors: ScheduleErrors;
}

// TODO REFACTOR THE WHOLE THING WITH REDUX FORMS

class ScheduleTemplate extends React.Component<Props, State> {
  static defaultProps = {
    fixedAddForm: false,
    isAddSchedule: false,
  };

  formRef: RefObject<HTMLFormElement>;
  input: HTMLElement | null;

  constructor(props: Props) {
    super(props);
    this.state = {
      saveLoading: false,
      schedule: props.schedule,
      selectedPeriodOption: this.getPeriodOption(this.props.schedule),
      scheduleId: null,
      errors: {
        ask: false,
        about: false,
        repetition: false,
        reminder: false,
        share: false,
        openEnded: false,
        deadline: false,
      },
    };
    this.input = null;
    this.formRef = React.createRef();
  }

  getPeriodOption = (schedule: ScheduleI) => {
    if (schedule.active && schedule.startDate && !schedule.dueDate && !schedule.repetition) {
      return SchedulePeriods.OpenEnded;
    }

    if (schedule.dueDate && !schedule.repetition) {
      return SchedulePeriods.DeadLine;
    }

    if (schedule.repetition) {
      return SchedulePeriods.Repeated;
    }

    if (schedule.customCycles?.length && !schedule.repetition) {
      return SchedulePeriods.Custom;
    }

    return SchedulePeriods.DeadLine;
  };

  // onChange handler for the Togglebox
  onChangeBox = (active: boolean) => {
    const newSchedule = { ...this.state.schedule, anonymous: active };
    if (!active) {
      newSchedule.isAnonymousManagerAnswerVisible = false;
    }
    this.setState({ schedule: newSchedule });
  };

  setInputRef = (node: HTMLInputElement) => {
    if (node) {
      this.input = node;
    }
    this.props.setInput(node);
  };

  getPeople = (people: Segment[], key: string) => {
    this.setState({ schedule: { ...this.state.schedule, [key]: [...people] } });
    if (people.length > 1) {
      this.deleteError(key);
    }
  };

  changeFlag = (value: boolean, key: keyof ScheduleI) => {
    this.setState({ schedule: { ...this.state.schedule, [key]: value } });
  };

  changeManagersLevels = (value: number) => {
    this.setState({schedule: {
      ...this.state.schedule,
        shareWithManagersLevels: value,
        shareWithManagers: Boolean(value)}
    });
  }

  // Set back the state to previous props
  cancelChanges = () => {
    this.props.history.goBack();
  };

  // onChange handler for the cycleSettings
  changeCycleSettings = (periodOption: SchedulePeriods, diff?: Partial<ScheduleI>) => {
    if (diff) {
      this.setState({
        schedule: {
          ...this.state.schedule,
          ...diff,
        },
      });
    }
    this.setState({ selectedPeriodOption: periodOption });
  };

  // onChange handler for activity of the Schedule
  changeActivity = (active: boolean) => {
    this.setState({ schedule: { ...this.state.schedule, active } });
  };

  checkForAboutError = () => {
    if (
      this.props.survey.subject === SurveySubjectTypes.General ||
      this.props.survey.useCase === SurveyUseCase.TopDown
    ) {
      return false;
    }
    return !(this.state.schedule.about && this.state.schedule.about.length > 0);
  };

  checkForAskError = () => {
    if (this.props.survey.useCase === SurveyUseCase.BottomUp) {
      return false;
    }
    if (this.state.schedule.ask) {
      if (this.state.schedule.ask.length > 0) {
        return false;
      }
    }
    return (
      !this.state.schedule.selfAssessment &&
      !this.state.schedule.selfSelection &&
      !this.state.schedule.managersSelection &&
      !this.state.schedule.askManagersOfAbouts &&
      !this.state.schedule.askTeamMembersOfAbouts
    );
  };

  checkForReminderError = () => {
    if (this.state.selectedPeriodOption === SchedulePeriods.Repeated) {
      if (this.state.schedule.reminder && this.state.schedule.repetition) {
        return this.state.schedule.reminder >= this.state.schedule.repetition;
      }
    }
    return false;
  };

  checkForDeadLineError = () => {
    if (this.state.selectedPeriodOption === SchedulePeriods.DeadLine) {
      if (this.state.schedule.startDate) {
        return !this.state.schedule.dueDate || this.state.schedule.dueDate <= this.state.schedule.startDate;
      }
    }
    return false;
  };

  checkForErrors = () => {
    return new Promise<void>((resolve) => {
      if (!this.state.schedule.active) {
        return resolve();
      }

      const startDate = getScheduleStartDate(this.state.schedule);

      this.setState(
        {
          errors: {
            ...this.state.errors,
            deadline: this.checkForDeadLineError(),
            ask: this.checkForAskError(),
            share:
              (!this.state.schedule.share || this.state.schedule.share.length < 1) &&
              !this.state.schedule.shareWithManagersLevels &&
              !this.state.schedule.shareWithSubject,
            about: this.checkForAboutError(),
            reminder: this.checkForReminderError(),
            repetition:
              this.state.selectedPeriodOption === SchedulePeriods.Repeated &&
              (this.state.schedule.repetition === null || !this.state.schedule.repetition),
            openEnded: !startDate,
          },
        },
        () => {
          resolve();
        },
      );
    });
  };

  async saveSchedule() {
    const sendingSchedule = { ...this.state.schedule };
    // Check for errors to display
    await this.checkForErrors();
    // Setting the irrelevant sections of the object to null
    // if the selected cycle setting does not need that
    if (this.state.selectedPeriodOption === SchedulePeriods.OpenEnded) {
      sendingSchedule.repetition = null;
      sendingSchedule.reminder = null;
      sendingSchedule.dueDate = null;
      sendingSchedule.customCycles = [];
    } else if (this.state.selectedPeriodOption === SchedulePeriods.DeadLine) {
      if (
        sendingSchedule.active &&
        sendingSchedule.dueDate &&
        sendingSchedule.startDate &&
        sendingSchedule.dueDate > sendingSchedule.startDate
      ) {
        sendingSchedule.repetition = null;
      }
      sendingSchedule.customCycles = [];
    } else if (this.state.selectedPeriodOption === SchedulePeriods.Repeated) {
      if (
        sendingSchedule.active &&
        sendingSchedule.repetition &&
        (!sendingSchedule.reminder || sendingSchedule.reminder <= sendingSchedule.repetition)
      ) {
        sendingSchedule.dueDate = null;
      }
      sendingSchedule.customCycles = [];
    } else if (this.state.selectedPeriodOption === SchedulePeriods.Custom) {
      sendingSchedule.startDate = null;
      sendingSchedule.repetition = null;
      sendingSchedule.dueDate = null;
      sendingSchedule.customCycles = sendingSchedule.customCycles?.filter(
        (cycle) => cycle.cycleStatus !== CycleStatus.InActive,
      );
      if (sendingSchedule.reminder) {
        sendingSchedule.dueDate = null;
      }
    }

    this.sendToBackend(sendingSchedule);
  }

  validate(sendingData: ScheduleI) {
    // if not yet active skip validation
    if (!sendingData.active) {
      return true;
    }

    // if (!sendingData.startDate) {
    //   return false;
    // }

    // Check if there is someone to share or sharewithsubject oder with managers
    if (!(sendingData.share || sendingData.shareWithSubject || sendingData.shareWithManagersLevels)) {
      return false;
    }

    // check if ask or about isnt empty
    if (
      this.props.survey.useCase === SurveyUseCase.ThreeSixty ||
      this.props.survey.subject === SurveySubjectTypes.Groups
    ) {
      if (sendingData.ask && sendingData.about) {
        // check if its not just an empty array
        if (
          (sendingData.ask.length > 0 ||
            sendingData.selfAssessment ||
            sendingData.selfSelection ||
            sendingData.managersSelection ||
            sendingData.askManagersOfAbouts ||
            sendingData.askTeamMembersOfAbouts) &&
          sendingData.about.length > 0
        ) {
          if (sendingData.share.length > 0 || sendingData.shareWithManagersLevels || sendingData.shareWithSubject) {
            return true;
          }
        }
      }
    }

    if (
      this.props.survey.useCase === SurveyUseCase.TopDown ||
      this.props.survey.subject === SurveySubjectTypes.General
    ) {
      if (sendingData.share.length > 0) {
        if (sendingData.ask.length > 0) {
          return true;
        }
      } else if (sendingData.shareWithManagersLevels || sendingData.shareWithSubject) {
        if (sendingData.ask.length > 0) {
          return true;
        }
      }
    }

    if (this.props.survey.useCase === SurveyUseCase.BottomUp) {
      if (sendingData.share.length > 0) {
        if (sendingData.about.length > 0) {
          return true;
        }
      } else if (sendingData.shareWithManagersLevels || sendingData.shareWithSubject) {
        if (sendingData.about.length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  hideAndRefresh = () => {
    this.props.history.goBack();
    this.setState({ saveLoading: false });
  };

  getSaveScheduleNotification = ({ startDate, active }: ScheduleI): NotificationI => {
    const dateNow = new Date();
    const scopeKey = this.props.survey.scope !== SurveyScopes.Admin ? 'request' : 'schedule';
    let txtKey = `x.surveys.schedules.${scopeKey}_updated_message`;
    const txtProps: { [key: string]: string | number } = {
      name: translateContent(this.props.survey.name),
    };
    if (startDate && active) {
      const startDateMoment = moment(startDate);
      txtKey = `x.surveys.schedules.${scopeKey}`;
      if (startDateMoment.isBefore(dateNow, 'days') || startDateMoment.isSame(dateNow, 'day')) {
        txtKey += '_updated_start_today_message';
      } else if (startDateMoment.isSame(moment(dateNow).add(1, 'day'), 'days')) {
        txtKey += '_updated_startdate_message';
      } else {
        txtKey += '_updated_startdate_message';
        txtProps.count = startDateMoment.diff(dateNow, 'days') + 1;
      }
    }
    return { id: txtKey, txtKey, txtProps };
  };

  sendToBackend(schedule: ScheduleI) {
    const dataToSend: ScheduleI & { managerAnonymity: boolean; cyclesToDelete?: string[] } = {
      ...schedule,
      managerAnonymity: schedule.isAnonymousManagerAnswerVisible,
    };

    const deletedCycles = this.props.schedule.customCycles?.filter(isCustomCycle).filter(
      cycle =>
        cycle.cycleStatus !== CycleStatus.InActive &&
        !dataToSend.customCycles?.filter(isCustomCycle).find(changedCycle => changedCycle.id === cycle.id),
    );
    dataToSend.cyclesToDelete = deletedCycles?.map(c => c.id);

    // updating a new schedule
    const isErrored = Object.values(this.state.errors).some(err => err);
    if (!this.validate(dataToSend) || isErrored) {
      this.setState({ saveLoading: false });
      return false;
    }

    this.setState({ saveLoading: true });

    dataToSend.anonymityMinAnswers = this.props.anonymityMinAnswers;

    if (!this.props.isAddSchedule || this.state.scheduleId) {
      if (this.state.scheduleId) {
        dataToSend.id = this.state.scheduleId;
      }
      apiCallPut(dataToSend)
        .then(() => {
          this.props.showNotification(this.getSaveScheduleNotification(dataToSend));
          this.hideAndRefresh();
        })
        .catch((error) => {
          if (isAxiosError(error)) {
            logger.info('Error putting Schedule', {
              error,
              info: {
                componentStack: 'backendActions.ts',
              },
            });
          } else {
            logger.error('Error putting Schedule: ' + error, {})
          }
          this.setState({ saveLoading: false, errors: { ...this.state.errors, unknownError: true } });
        });
    } else {
      apiCallPost(dataToSend, this.props.survey.id)
        .then(() => {
          this.props.showNotification(this.getSaveScheduleNotification(dataToSend));
          this.hideAndRefresh();
        })
        .catch((error) => {
          if (isAxiosError(error)) {
            logger.info('Error creating Schedule', {
              error,
              info: {
                componentStack: 'backendActions.ts',
              },
            });
          } else {
            logger.error('Error creating Schedule: ' + error, {})
          }
          this.setState({ saveLoading: false, errors: { ...this.state.errors, unknownError: true } });
        });
    }
    return null;
  }

  deleteError = (error: string) => {
    this.setState({ errors: { ...this.state.errors, [error]: false } });
  };

  submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void this.saveSchedule();
  };

  getShowButtons = () => {
    const savedScheduleDiff: ScheduleI = { ...this.props.schedule };
    const editedScheduleDiff: ScheduleI = { ...this.state.schedule };

    const customSelectorsDiff = differenceBy(
      editedScheduleDiff.customSelectors,
      savedScheduleDiff.customSelectors,
      'id',
    );

    const isPeriodChanged = this.getPeriodOption(savedScheduleDiff) !== this.state.selectedPeriodOption;
    const isCyclesChanged =
      savedScheduleDiff.customCycles?.length !== editedScheduleDiff.customCycles?.length ||
      savedScheduleDiff.customCycles?.some(
        (savedCycle, index) =>
          savedCycle.dueDate !== editedScheduleDiff.customCycles?.[index].dueDate ||
          savedCycle.startDate !== editedScheduleDiff.customCycles?.[index].startDate,
      );

    // @ts-expect-error we want to compare with exception of "active" property. too much typing hassle for now
    delete savedScheduleDiff.active;
    // @ts-expect-error too much typing hassle for now
    delete editedScheduleDiff.active;


    return (
      !eq(editedScheduleDiff, savedScheduleDiff) ||
      this.props.isAddSchedule ||
      isPeriodChanged ||
      customSelectorsDiff.length > 0 ||
      Boolean(isCyclesChanged)
    );
  };

  handleChangeManagerAnswerVisible = (isVisible: boolean) => {
    this.setState({
      schedule: {
        ...this.state.schedule,
        isAnonymousManagerAnswerVisible: isVisible,
      },
    });
  };

  handleChangeCycles = (cycles: Array<NewCustomCycle | CustomCycle>) => {
    let startDate = this.state.schedule.startDate;
    if (!startDate && cycles.length) {
      startDate = cycles[0].startDate;
    }

    this.setState({ schedule: { ...this.state.schedule, startDate, customCycles: cycles } });
  };

  handleChangeSchedule = (schedule: Partial<ScheduleI>) => {
    this.setState({ schedule: { ...this.state.schedule, ...schedule } });
  };

  render() {
    const { t } = this.props;
    const useCaseKey =
      this.props.survey.subject === SurveySubjectTypes.People ? `.${this.props.survey.useCase}` : '';
    const showAboutSection =
      this.props.survey.useCase !== SurveyUseCase.TopDown &&
      this.props.survey.subject !== SurveySubjectTypes.General;
    const showAskSection = this.props.survey.useCase !== SurveyUseCase.BottomUp;

    return (
      <SchedulesItemContent name="schedule" onSubmit={this.submitForm} ref={this.formRef}>
        <PeopleSectionContainer>
          {showAboutSection && (
            <AboutSection
              setInputRef={this.setInputRef}
              onChangeBox={this.onChangeBox}
              survey={this.props.survey}
              deleteError={this.deleteError}
              getAboutPeople={this.getPeople}
              error={this.state.errors.about}
              useCaseKey={useCaseKey}
              schedule={this.state.schedule}
              oldSchedule={this.props.schedule}
              changeFlag={this.changeFlag}
            />
          )}
          {showAskSection && (
            <AskSection
              setInputRef={!showAboutSection ? this.setInputRef : null}
              survey={this.props.survey}
              error={this.state.errors.ask}
              selectedPeople={this.state.schedule.ask}
              schedule={this.state.schedule}
              oldSchedule={this.props.schedule}
              deleteError={this.deleteError}
              getAskedPeople={this.getPeople}
              useCaseKey={useCaseKey}
              onChangeBox={this.onChangeBox}
              changeFlag={this.changeFlag}
              onChangeManagerAnswerVisible={this.handleChangeManagerAnswerVisible}
            />
          )}
          <ShareSection
            changeBoolean={this.changeFlag}
            changeManagersLevels={this.changeManagersLevels}
            changeAttributesVisibility={this.changeFlag}
            changeShowInHeatmapToprow={this.changeFlag}
            survey={this.props.survey}
            deleteError={this.deleteError}
            getSharedPeople={this.getPeople}
            error={this.state.errors.share}
            useCaseKey={useCaseKey}
            schedule={this.state.schedule}
          />
        </PeopleSectionContainer>
        <CycleContainer
          schedule={this.state.schedule}
          changingState={this.changeCycleSettings}
          errors={this.state.errors}
          deleteError={this.deleteError}
          isAddSchedule={this.props.isAddSchedule}
          selectedPeriodOption={this.state.selectedPeriodOption}
          cycles={this.state.schedule.customCycles ?? []}
          onChangeCycles={this.handleChangeCycles}
          onChangeSchedule={this.handleChangeSchedule}
        />
        <SurveyInvite schedule={this.state.schedule} />
        <ButtonSection
          t={t}
          cancelChanges={this.cancelChanges}
          showButtons={this.getShowButtons()}
          saveLoading={this.state.saveLoading}
          errors={this.state.errors}
          savedSchedule={this.props.schedule}
          editedSchedule={this.state.schedule}
          changeActivity={this.changeActivity}
          isAddSchedule={this.props.isAddSchedule}
        />
      </SchedulesItemContent>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => ({
  anonymityMinAnswers: getCompanyAttrValueBySlug(state, AttrSlugType.AnonymityMinAnswers),
  customCycles: (state.user.cycles && state.user.cycles.byId) || {},
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  showNotification: notification => dispatch(showNotification(notification)),
  fetchCycles: () => dispatch(fetchCycles()),
});

const ScheduleTemplateFH = formHoc(ScheduleTemplate);
const ScheduleTemplateR = withRouter(ScheduleTemplateFH);
const ScheduleTemplateC = connect(mapStateToProps, mapDispatch2Props)(ScheduleTemplateR);

export default ScheduleTemplateC;
